import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { formatDateNoTime } from "@sp-crm/core";
import { ConversationFeedBasicsFragment } from "generated/graphql";
import React, { useCallback, useState } from "react";
import { ContactSearchHitsLink } from "./contact-search-hits-link";

interface MailboxMessageThreadProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
}

export const MailboxMessageThread: React.FC<MailboxMessageThreadProps> = props => {
    const { thread } = props;
    const message = thread.mailboxMessage;
    const [viewing, setViewing] = useState(false);

    const handleView = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setViewing(!viewing);
        },
        [viewing],
    );

    const senderName = message?.sender?.name || message?.sender?.email || "Unknown";

    return (
        <li key={thread.id}>
            <div
                className="flex items-center gap-3 px-3 py-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                onClick={handleView}>
                <EnvelopeIcon
                    className="h-4 w-4 text-gray-400 flex-shrink-0"
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 truncate max-w-[120px]">
                        {senderName}
                    </span>
                    <ContactSearchHitsLink hits={thread.contactSearchHits} />
                    <span className="text-gray-400 hidden sm:inline">·</span>
                    <span className="text-sm text-gray-700 truncate hidden sm:inline">
                        {message?.subject || "(No subject)"}
                    </span>
                    <span className="text-sm text-gray-500 truncate hidden md:inline flex-1">
                        — {message?.bodyPreview}
                    </span>
                </div>
                <div className="flex items-center text-xs text-gray-500 flex-shrink-0">
                    <span>{formatDateNoTime(message?.receivedTimestamp)}</span>
                </div>
            </div>
            {viewing && (
                <div className="px-10 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="space-y-1 text-sm">
                        {message?.from && (
                            <div className="text-gray-600">
                                <span className="font-medium">From:</span>{" "}
                                {message.from.name
                                    ? `${message.from.name} <${message.from.email}>`
                                    : message.from.email}
                            </div>
                        )}
                        {message?.recipients && message.recipients.length > 0 && (
                            <div className="text-gray-600">
                                <span className="font-medium">To:</span>{" "}
                                {message.recipients
                                    .filter(r => r.type === "To")
                                    .map(r =>
                                        r.contact?.name
                                            ? `${r.contact.name} <${r.contact.email}>`
                                            : r.contact?.email,
                                    )
                                    .join(", ")}
                            </div>
                        )}
                        <div className="text-gray-600">
                            <span className="font-medium">Subject:</span>{" "}
                            {message?.subject || "(No subject)"}
                        </div>
                        {message?.bodyPreview && (
                            <div className="mt-3 pt-3 border-t border-gray-200 text-gray-700 whitespace-pre-wrap">
                                {message.bodyPreview}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
};
